import React from 'react';
import { AppModal } from './AppModal';
import { Button, Form, Input, Space, Typography } from '../';

import { useNotificationContext } from '../../providers/NotificationProvider';
import { ValidationErrors } from '../../constants';

import { useResetPasswordMutation } from '../../api/auth';
import { validatePassword, handleError } from '../../lib/helpers';

export const ResetPasswordModal = ({ open, onCancel }) => {
    const [form] = Form.useForm();

    const { notifySuccess, notifyError } = useNotificationContext();
    const [resetPassword, { isLoading }] = useResetPasswordMutation();

    const onFinish = async () => {
        try {
            const { code, password, confirmPassword } = await form.validateFields();
            await resetPassword({ code, password, confirmPassword }).unwrap();
            notifySuccess({
                message: 'Password Reset',
                description: 'Your password has been reset. You can now log in with your new password.',
                onClose: () => {
                    sessionStorage.removeItem('passwordResetToken');
                    onCancel();
                },
            });
        } catch (error) {
            handleError(error, notifyError, 'Error resetting password');
        }
    };

    return (
        <AppModal
            title="Reset Password"
            onCancel={onCancel}
            open={open}
            closable={false}
            maskClosable={false}
            footer={null}
        >
            <Typography.Paragraph>
                Please enter the password reset code you received in your email, along with your new password.
            </Typography.Paragraph>
            <Form
                form={form}
                name="reset-password"
                layout="vertical"
                initialValues={{ remember: true }}
                style={{ maxWidth: 600 }}
                onFinish={onFinish}
            >
                <Form.Item
                    name="code"
                    label="Password Reset Code"
                    rules={[{ required: true, message: ValidationErrors.resetCode }]}
                >
                    <Input placeholder="reset code" />
                </Form.Item>
                <Form.Item
                    name="password"
                    label="New Password"
                    rules={[
                        {
                            required: true,
                            message: ValidationErrors.password,
                        },
                        {
                            validator: (_, value) => {
                                const errors = [];
                                validatePassword(value, errors);
                                return errors.length
                                    ? Promise.reject(
                                          <>
                                              {errors.map((err, idx) => (
                                                  <span key={idx}>{err}</span>
                                              ))}
                                          </>,
                                      )
                                    : Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder="new password" type="password" />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    dependencies={['password']}
                    rules={[
                        { required: true, message: ValidationErrors.confirmPassword },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('The two passwords do not match!'));
                            },
                        }),
                    ]}
                >
                    <Input placeholder="confirm password" type="password" />
                </Form.Item>
                <Form.Item style={{ textAlign: 'right' }}>
                    <Space>
                        <Button key="cancel" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button key="submit" type="primary" loading={isLoading} htmlType="submit">
                            Reset Password
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </AppModal>
    );
};
